import { useState, useEffect } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Loader2, PieChart, MoreHorizontal, Pencil, Trash2, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_SHARES = 100000; // Authorized share count — ownership % is auto-calculated against this
const EMPTY = { name: '', ownership_pct: '', shares: '', share_class: 'common', founder_shares: '', preferred_shares: '', email: '', phone: '', notes: '' };

const calcPct = (shares) => {
  const n = parseFloat(shares);
  if (!n || isNaN(n) || n <= 0) return '';
  return ((n / TOTAL_SHARES) * 100).toFixed(4).replace(/\.?0+$/, '');
};

export default function ShareholdersPage() {
  const { currentCompany } = useCompany();
  const [shareholders, setShareholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [classFilter, setClassFilter] = useState('all'); // 'all' | 'common' | 'preferred' | 'cap'

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/shareholders/export/csv',
    importUrl: '/api/shareholders/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchShareholders(),
    entityName: 'shareholders'
  });

  useEffect(() => {
    if (currentCompany) fetchShareholders();
  }, [currentCompany]);

  const fetchShareholders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/shareholders`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setShareholders(res.data);
    } catch { toast.error('Failed to load shareholders'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!currentCompany?.id) { toast.error('No company selected. Please switch to Veriquant first.'); return; }
    setSubmitting(true);
    try {
      const payload = {
        company_id: currentCompany.id,
        name: form.name.trim(),
        ownership_pct: form.ownership_pct ? parseFloat(form.ownership_pct) : null,
        shares: form.shares ? parseFloat(form.shares) : null,
        share_class: form.share_class || 'common',
        founder_shares: form.founder_shares ? parseFloat(form.founder_shares) : null,
        preferred_shares: form.preferred_shares ? parseFloat(form.preferred_shares) : null,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes || null
      };
      await axios.post(`${API_URL}/api/shareholders`, payload);
      toast.success('Shareholder added');
      setIsAddOpen(false);
      setForm(EMPTY);
      fetchShareholders();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((e) => e?.msg || JSON.stringify(e)).join(' · ')
        : err?.message || 'Failed to add shareholder';
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        ownership_pct: form.ownership_pct ? parseFloat(form.ownership_pct) : null,
        shares: form.shares ? parseFloat(form.shares) : null,
        share_class: form.share_class || 'common',
        founder_shares: form.founder_shares ? parseFloat(form.founder_shares) : null,
        preferred_shares: form.preferred_shares ? parseFloat(form.preferred_shares) : null,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes || null
      };
      await axios.put(`${API_URL}/api/shareholders/${selected.id}`, payload);
      toast.success('Shareholder updated');
      setIsEditOpen(false);
      setSelected(null);
      setForm(EMPTY);
      fetchShareholders();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((e) => e?.msg || JSON.stringify(e)).join(' · ')
        : err?.message || 'Failed to update shareholder';
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/shareholders/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Shareholder deleted');
      setDeleteTarget(null);
      fetchShareholders();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (sh) => {
    setSelected(sh);
    setForm({ name: sh.name, ownership_pct: sh.ownership_pct?.toString() || '', shares: sh.shares?.toString() || '', share_class: sh.share_class || 'common', founder_shares: sh.founder_shares?.toString() || '', preferred_shares: sh.preferred_shares?.toString() || '', email: sh.email || '', phone: sh.phone || '', notes: sh.notes || '' });
    setIsEditOpen(true);
  };

  const totalOwnership = shareholders.reduce((s, sh) => s + (sh.ownership_pct || 0), 0);

  if (!currentCompany) return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;

  // NOTE: this MUST be a plain JSX value, NOT a nested React component.
  // Defining it as `const ShareholderForm = () => (...)` causes a brand-new
  // component type to be created on every render, which unmounts/remounts the
  // inputs and makes them lose focus after every keystroke (1-char-at-a-time bug).
  const shareholderForm = (
    <div className="space-y-4 py-2">
      <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Shareholder name" data-testid="shareholder-name-input" /></div>
      <div className="space-y-2">
        <Label>Share Class</Label>
        <RadioGroup
          value={form.share_class}
          onValueChange={(v) => setForm({ ...form, share_class: v })}
          className="flex gap-6 flex-wrap"
          data-testid="shareholder-class-radio"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="common" id="sc-common" data-testid="share-class-common" />
            <Label htmlFor="sc-common" className="font-normal cursor-pointer">Common</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="preferred" id="sc-preferred" data-testid="share-class-preferred" />
            <Label htmlFor="sc-preferred" className="font-normal cursor-pointer">Preferred</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Ownership % <span className="text-xs text-gray-400 font-normal">(auto)</span></Label>
          <Input
            type="text"
            value={form.shares ? `${calcPct(form.shares)}%` : ''}
            readOnly
            placeholder="—"
            className="bg-gray-50 text-gray-700"
            data-testid="shareholder-pct-input"
          />
          <p className="text-xs text-gray-400">Of {TOTAL_SHARES.toLocaleString()} authorized shares</p>
        </div>
        <div className="space-y-2">
          <Label>Shares</Label>
          <Input
            type="number"
            step="1"
            value={form.shares}
            onChange={(e) => {
              const v = e.target.value;
              setForm({ ...form, shares: v, ownership_pct: calcPct(v) });
            }}
            placeholder="e.g. 1000"
            data-testid="shareholder-shares-input"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="founder-shares">Founder Shares</Label>
        <Input
          id="founder-shares"
          type="number"
          step="1"
          value={form.founder_shares}
          onChange={(e) => setForm({ ...form, founder_shares: e.target.value })}
          placeholder="e.g. 4000 (optional)"
          data-testid="shareholder-founder-shares-input"
        />
        <p className="text-xs text-gray-500">Leave blank if this shareholder has no founder shares.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="preferred-shares">Preferred Shares (Series A Convertible)</Label>
        <Input
          id="preferred-shares"
          type="number"
          step="1"
          value={form.preferred_shares}
          onChange={(e) => setForm({ ...form, preferred_shares: e.target.value })}
          placeholder="e.g. 150 (optional)"
          data-testid="shareholder-preferred-shares-input"
        />
        <p className="text-xs text-gray-500">Leave blank if this shareholder has no Series A / preferred shares.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" /></div>
        <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" /></div>
      </div>
      <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." /></div>
    </div>
  );

  const filteredShareholders = classFilter === 'all'
    ? shareholders
    : shareholders.filter((sh) => (sh.share_class || 'common') === classFilter);
  const commonCount = shareholders.filter((sh) => (sh.share_class || 'common') === 'common').length;
  const preferredCount = shareholders.filter((sh) => sh.share_class === 'preferred').length;
  const totalFounderShares = shareholders.reduce((s, sh) => s + (sh.founder_shares || 0), 0);
  const founderHolders = shareholders.filter((sh) => (sh.founder_shares || 0) > 0);

  return (
    <div className="space-y-6" data-testid="shareholders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Shareholders</h1>
          <p className="text-gray-500 mt-1">Manage ownership and equity</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="shareholders" />
          <Button onClick={() => { setForm(EMPTY); setIsAddOpen(true); }} data-testid="add-shareholder-btn"><Plus className="w-4 h-4 mr-2" />Add Shareholder</Button>
        </div>
      </div>

      {/* Ownership Summary */}
      {shareholders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <PieChart className="w-5 h-5 text-[#1E40AF]" />
            <h2 className="font-medium text-gray-900">Ownership Summary</h2>
          </div>
          <div className="flex flex-wrap gap-4">
            {shareholders.map(sh => (
              <div key={sh.id} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#1E40AF]" style={{ opacity: Math.max(0.3, (sh.ownership_pct || 0) / 100) }} />
                <span className="text-sm text-gray-700">{sh.name}</span>
                <span className="text-sm font-medium text-gray-900">{sh.ownership_pct != null ? `${sh.ownership_pct}%` : '-'}</span>
              </div>
            ))}
            <div className="ml-auto flex items-center gap-2 border-l pl-4 border-gray-200">
              <span className="text-sm text-gray-500">Total:</span>
              <span className={`text-sm font-semibold ${totalOwnership > 100 ? 'text-red-600' : 'text-gray-900'}`}>{totalOwnership.toFixed(2)}%</span>
            </div>
          </div>
          {totalFounderShares > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span className="text-sm text-gray-500">Founder Shares Outstanding:</span>
                <span className="text-sm font-semibold text-purple-700" data-testid="total-founder-shares">{totalFounderShares.toLocaleString()}</span>
              </div>
              {founderHolders.map((sh) => (
                <div key={sh.id} className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{sh.name}:</span>
                  <span className="font-medium text-gray-900">{(sh.founder_shares || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : shareholders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <PieChart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No shareholders yet</h3>
          <p className="text-gray-500 mb-4">Add your first shareholder</p>
          <Button onClick={() => { setForm(EMPTY); setIsAddOpen(true); }}><Plus className="w-4 h-4 mr-2" />Add Shareholder</Button>
        </div>
      ) : (
        <>
          {/* Share Class filter tabs */}
          <Tabs value={classFilter} onValueChange={setClassFilter} className="w-full" data-testid="share-class-tabs">
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All ({shareholders.length})</TabsTrigger>
              <TabsTrigger value="common" data-testid="tab-common">Common ({commonCount})</TabsTrigger>
              <TabsTrigger value="preferred" data-testid="tab-preferred">Preferred ({preferredCount})</TabsTrigger>
              <TabsTrigger value="cap" data-testid="tab-cap">Cap Table</TabsTrigger>
            </TabsList>
          </Tabs>

        {classFilter === 'cap' ? (
          /* Cap Table view */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="cap-table-view">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Capitalization Table</h3>
                <p className="text-xs text-gray-500 mt-0.5">Veriquant Asset Management — Summary</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-purple-600 uppercase">Founders</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-amber-600 uppercase">Series A<br/>Convertible</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase">Employee<br/>Vesting</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Shares</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ownership %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shareholders.map((sh) => {
                    const f = sh.founder_shares || 0;
                    const p = sh.preferred_shares || 0;
                    const c = sh.shares || 0;
                    const total = f + p + c;
                    const pct = sh.ownership_pct;
                    return (
                      <tr key={sh.id} className="hover:bg-gray-50" data-testid={`cap-row-${sh.id}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{sh.name}</td>
                        <td className="px-4 py-3 text-right text-sm">{f > 0 ? <span className="text-purple-700 font-medium">{f.toLocaleString()}</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right text-sm">{p > 0 ? <span className="text-amber-700 font-medium">{p.toLocaleString()}</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right text-sm">{c > 0 ? <span className="text-blue-700 font-medium">{c.toLocaleString()}</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{total.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#1E40AF] rounded-full" style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
                            <span className="text-sm font-semibold text-gray-900 w-20 text-right">{pct != null ? `${pct.toFixed(4)}%` : '—'}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  {(() => {
                    const sumF = shareholders.reduce((s, sh) => s + (sh.founder_shares || 0), 0);
                    const sumP = shareholders.reduce((s, sh) => s + (sh.preferred_shares || 0), 0);
                    const sumC = shareholders.reduce((s, sh) => s + (sh.shares || 0), 0);
                    const sumTotal = sumF + sumP + sumC;
                    const sumPct = shareholders.reduce((s, sh) => s + (sh.ownership_pct || 0), 0);
                    return (
                      <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                        <td className="px-4 py-3 text-gray-900">Totals</td>
                        <td className="px-4 py-3 text-right text-sm text-purple-700">{sumF.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm text-amber-700">{sumP.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm text-blue-700">{sumC.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{sumTotal.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{sumPct.toFixed(4)}%</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ownership %</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shares</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Founder Shares</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredShareholders.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-gray-400">No {classFilter} shareholders.</td></tr>
              ) : filteredShareholders.map((sh) => (
                <tr key={sh.id} className="hover:bg-gray-50" data-testid={`shareholder-row-${sh.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{sh.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                      sh.share_class === 'preferred' ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                    }`}>{sh.share_class || 'common'}</span>
                  </td>
                  <td className="px-4 py-3">
                    {sh.ownership_pct != null ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#1E40AF] rounded-full" style={{ width: `${Math.min(100, sh.ownership_pct)}%` }} /></div>
                        <span className="text-sm font-medium text-gray-900">{sh.ownership_pct}%</span>
                      </div>
                    ) : <span className="text-gray-400 text-sm">-</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-sm">{sh.shares != null ? sh.shares.toLocaleString() : '-'}</td>
                  <td className="px-4 py-3 text-sm">{(sh.founder_shares || 0) > 0 ? <span className="font-medium text-purple-700">{sh.founder_shares.toLocaleString()}</span> : <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-3">{sh.email ? <a href={`mailto:${sh.email}`} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{sh.email}</a> : <span className="text-gray-400 text-sm">-</span>}</td>
                  <td className="px-4 py-3">{sh.phone ? <span className="text-sm text-gray-600 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{sh.phone}</span> : <span className="text-gray-400 text-sm">-</span>}</td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(sh)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(sh)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent data-testid="add-shareholder-dialog"><DialogHeader><DialogTitle>Add Shareholder</DialogTitle><DialogDescription>Add a new shareholder.</DialogDescription></DialogHeader>{shareholderForm}<DialogFooter><Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button><Button onClick={handleAdd} disabled={submitting} data-testid="save-shareholder-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent><DialogHeader><DialogTitle>Edit Shareholder</DialogTitle></DialogHeader>{shareholderForm}<DialogFooter><Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button><Button onClick={handleEdit} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Shareholder</AlertDialogTitle><AlertDialogDescription>Delete "{deleteTarget?.name}"?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
